import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, collection, getDocs, query, where, orderBy, limit, writeBatch } from 'firebase/firestore';
import { MOUNTAIN_LISTS } from '../data/mountainData';
import { toPlainText } from '../utils/plainText';
import { showToast } from '../utils/toast';
import './Profile.css';


function Profile() {
  const { currentUser, logout, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [userStats, setUserStats] = useState({});
  const [climbedMountains, setClimbedMountains] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatMembershipDuration = (createdAt) => {
    const createdDate = createdAt?.toDate ? createdAt.toDate() : createdAt ? new Date(createdAt) : null;
    if (!createdDate || Number.isNaN(createdDate.getTime())) return '不明';
    const diffDays = Math.max(0, Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24)));
    if (diffDays < 30) return `${diffDays + 1}日`;
    const months = Math.floor(diffDays / 30);
    if (months < 12) return `${months}ヶ月`;
    const years = Math.floor(months / 12);
    const restMonths = months % 12;
    return restMonths ? `${years}年${restMonths}ヶ月` : `${years}年`;
  };

  const fetchProfile = useCallback(async () => {
    try {
      const [profileDoc, statsDoc, logsSnap, reviewsSnap] = await Promise.all([
        getDoc(doc(db, 'users', currentUser.uid)),
        getDoc(doc(db, 'userStats', currentUser.uid)),
        getDocs(query(collection(db, 'users', currentUser.uid, 'mountainLogs'), where('climbed', '==', true))),
        getDocs(query(collection(db, 'reviews'), where('revieweeId', '==', currentUser.uid), orderBy('createdAt', 'desc'), limit(20)))
      ]);

      if (profileDoc.exists()) {
        setProfile(profileDoc.data());
      }
      setUserStats(statsDoc.exists() ? statsDoc.data() : {});

      // 登頂済み山名を解決（ローカルデータから名前を引く）
      const mountainNameMap = {};
      Object.values(MOUNTAIN_LISTS).forEach(list => {
        (list.mountains || []).forEach(m => {
          mountainNameMap[`${list.shortName || list.name}_${m.no}`] = { name: m.name, listName: list.shortName || list.name };
        });
      });
      const climbed = logsSnap.docs.map(d => {
        const { listId, no } = d.data();
        const key = `${listId}_${no}`;
        return mountainNameMap[key] || { name: `${listId} #${no}`, listName: listId };
      }).filter(Boolean);
      setClimbedMountains(climbed);
      setReviews(reviewsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('プロフィールの取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchProfile();
    }
  }, [currentUser, fetchProfile]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('ログアウトに失敗しました:', error);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('アカウントを削除すると、すべてのデータが失われます。本当に削除してもよろしいですか？')) return;
    if (!window.confirm('この操作は取り消せません。本当にアカウントを削除しますか？')) return;

    const isGoogle = currentUser.providerData[0]?.providerId === 'google.com';
    let password = '';
    if (!isGoogle) {
      password = window.prompt('確認のため、現在のパスワードを入力してください。');
      if (!password) return;
    }

    try {
      const uid = currentUser.uid;
      const batch = writeBatch(db);

      // 1. 自分の応募データを削除
      const appsSnap = await getDocs(query(collection(db, 'applications'), where('userId', '==', uid)));
      appsSnap.docs.forEach(d => batch.delete(d.ref));

      // 2. 自分の募集データを削除
      const recsSnap = await getDocs(query(collection(db, 'recruitments'), where('userId', '==', uid)));
      recsSnap.docs.forEach(d => batch.delete(d.ref));

      // 3. 自分宛の通知を削除
      const notifsSnap = await getDocs(query(collection(db, 'notifications'), where('recipientId', '==', uid)));
      notifsSnap.docs.forEach(d => batch.delete(d.ref));

      // 4. ブロックデータを削除
      const blocksSnap = await getDocs(query(collection(db, 'blocks'), where('blockerId', '==', uid)));
      blocksSnap.docs.forEach(d => batch.delete(d.ref));

      // 5. users/{uid} ドキュメントを削除
      batch.delete(doc(db, 'users', uid));
      batch.delete(doc(db, 'userStats', uid));

      await batch.commit();

      // 6. Firebase Auth ユーザーを削除（再認証が必要）
      await deleteAccount(password);
      showToast({ message: 'アカウントを削除しました。', type: 'success' });
      window.location.href = '/';
    } catch (err) {
      console.error('アカウントの削除に失敗しました:', err);
      if (err.code === 'auth/wrong-password') {
        showToast({ message: 'パスワードが正しくありません。', type: 'error' });
      } else if (err.code === 'auth/popup-closed-by-user') {
        showToast({ message: 'Google認証がキャンセルされました。', type: 'info' });
      } else {
        showToast({ message: 'アカウントの削除に失敗しました。もう一度お試しください。', type: 'error' });
      }
    }
  };

  if (loading) {
    return <div className="loading">読み込み中...</div>;
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>プロフィール</h1>
      </div>

      <div className="profile-content">
        <div className="main-info">
          <div className="card">
            <div className="card-header">
              <h2>基本情報</h2>
              <Link to="/profile/edit" className="btn btn-outline">
                プロフィール編集
              </Link>
            </div>
            
            <div className="card-body">
              {profile?.photoURL && (
                <div style={{display:'flex', justifyContent:'center', marginBottom:'16px'}}>
                  <img
                    src={profile.photoURL}
                    alt="プロフィール"
                    style={{width:'96px', height:'96px', borderRadius:'50%', objectFit:'cover', border:'2px solid var(--primary-green)'}}
                  />
                </div>
              )}

              <div className="info-grid">
                <div className="info-item">
                  <label>ニックネーム</label>
                  <p>{profile?.nickname || '未設定'}</p>
                </div>
                
                <div className="info-item">
                  <label>メールアドレス</label>
                  <p>{currentUser?.email}</p>
                </div>
                
                <div className="info-item">
                  <label>登録日</label>
                  <p>
                    {profile?.createdAt ? 
                      new Date(profile.createdAt.toDate ? profile.createdAt.toDate() : profile.createdAt).toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 
                      '不明'
                    }
                  </p>
                </div>
              </div>

              <div className="public-profile-section">
                <div className="public-profile-header">
                  <label>参加実績・継続期間</label>
                </div>
                <div className="public-profile-grid">
                  <div className="public-profile-item">
                    <label>参加実績</label>
                    <p>{userStats.completedHikeCount || 0}回</p>
                  </div>
                  <div className="public-profile-item">
                    <label>応募数</label>
                    <p>{userStats.appliedCount || 0}回</p>
                  </div>
                  <div className="public-profile-item">
                    <label>募集作成数</label>
                    <p>{userStats.recruitedCount || 0}回</p>
                  </div>
                  <div className="public-profile-item">
                    <label>継続期間</label>
                    <p>{formatMembershipDuration(profile?.createdAt)}</p>
                  </div>
                </div>
              </div>

              {profile?.bio && (
                <div className="bio-section">
                  <label>自己紹介</label>
                  <p className="bio">{toPlainText(profile.bio)}</p>
                </div>
              )}


              {profile?.climbingHistory && (
                <div className="history-section">
                  <label>登山歴</label>
                  <p className="history">{toPlainText(profile.climbingHistory)}</p>
                </div>
              )}

              {profile?.favoriteMountains && (
                <div className="favorites-section">
                  <label>好きな山</label>
                  <p className="favorites">{toPlainText(profile.favoriteMountains)}</p>
                </div>
              )}

              {climbedMountains.length > 0 && (
                <div className="climbed-section">
                  <label>登頂記録（{climbedMountains.length}座）</label>
                  <ul className="climbed-list">
                    {climbedMountains.map((m, i) => (
                      <li key={i}>{m.name} <span className="climbed-list-name">（{m.listName}）</span></li>
                    ))}
                  </ul>
                </div>
              )}

              {profile?.nearestStation && (
                <div className="nearest-station-section">
                  <label>最寄り駅</label>
                  <p className="nearest-station">{profile.nearestStation}</p>
                  <p className="privacy-note">※ 応募先の募集者のみに公開されます</p>
                </div>
              )}

              <div className="public-profile-section">
                <div className="public-profile-header">
                  <label>基本情報</label>
                </div>
                <div className="public-profile-grid">
                  <div className="public-profile-item">
                    <label>性別</label>
                    <p>{profile?.gender || '未回答'}</p>
                  </div>
                  <div className="public-profile-item">
                    <label>年代</label>
                    <p>{profile?.ageGroup || '未回答'}</p>
                  </div>
                  <div className="public-profile-item">
                    <label>居住都道府県</label>
                    <p>{profile?.residencePrefecture || '未回答'}</p>
                  </div>
                  <div className="public-profile-item">
                    <label>マイカー</label>
                    <p>{profile?.hasCar ? '🚗 所有' : '所有なし'}</p>
                  </div>
                  <div className="public-profile-item">
                    <label>加入登山保険</label>
                    <p>{profile?.mountainInsurance || '未設定'}</p>
                  </div>
                </div>
              </div>

              <div className="private-profile-section">
                <div className="private-profile-header">
                  <span className="private-badge">🔒 非公開プロフィール</span>
                  <p className="privacy-note">以下はヤマッチで応募・募集時のみ相手に公開されます</p>
                </div>
                <div className="private-profile-grid">
                  <div className="private-item">
                    <label>ヘリコプター救助サービス番号</label>
                    <p>{profile?.cocoheriNumber || '未設定'}</p>
                  </div>
                </div>
                <p className="privacy-note">※ 非公開情報のスクリーンショット撮影、転載、第三者への共有は禁止です。</p>
              </div>

              {(profile?.monthlyFrequency || profile?.pace || profile?.fitnessLevel ||
                (profile?.hikingStyle || []).length > 0) && (
                <div className="experience-section" style={{marginTop:'16px'}}>
                  <label>登山スタイル</label>
                  <div style={{display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'8px'}}>
                    {profile.monthlyFrequency && profile.monthlyFrequency !== '未回答' && (
                      <span className="exp-tag exp-tag--checked">頻度: {profile.monthlyFrequency}</span>
                    )}
                    {(profile.hikingStyle || []).map(s => {
                      const labels = {dayHike:'日帰り',hutStay:'山小屋泊',tentStay:'テント泊',trail:'トレラン',photo:'写真'};
                      return <span key={s} className="exp-tag exp-tag--checked">{labels[s] || s}</span>;
                    })}
                    {profile.pace && profile.pace !== '未回答' && (
                      <span className="exp-tag exp-tag--checked">ペース: {profile.pace}</span>
                    )}
                    {profile.fitnessLevel && profile.fitnessLevel !== '未回答' && (
                      <span className="exp-tag exp-tag--checked">体力: {profile.fitnessLevel}</span>
                    )}
                  </div>
                </div>
              )}

              {((profile?.altitudeExperience || []).length > 0 ||
                (profile?.technicalExperience || []).length > 0) && (
                <div className="experience-section" style={{marginTop:'16px'}}>
                  <label>技術・経験</label>
                  <div style={{display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'8px'}}>
                    {(profile.altitudeExperience || []).map(k => {
                      const labels = {lowMountain:'低山（〜1000m）',midMountain:'中山（1000〜2000m）',highMountain:'高山（2000〜3000m）',alpineMountain:'3000m超'};
                      return <span key={k} className="exp-tag exp-tag--checked">{labels[k] || k}</span>;
                    })}
                    {(profile.technicalExperience || []).map(k => {
                      const labels = {chainField:'鎖場経験',rockField:'岩場経験',snowMountain:'雪山経験',crampon:'アイゼン使用',iceAxe:'ピッケル使用'};
                      return <span key={k} className="exp-tag exp-tag--checked">{labels[k] || k}</span>;
                    })}
                  </div>
                </div>
              )}

              {(profile?.ownedGear || []).length > 0 && (
                <div className="experience-section" style={{marginTop:'16px'}}>
                  <label>所持装備</label>
                  <div style={{display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'8px'}}>
                    {(profile.ownedGear || []).map(k => {
                      const labels = {helmet:'ヘルメット',crampon:'アイゼン',iceAxe:'ピッケル',tent:'テント泊装備',winterGear:'雪山装備'};
                      const links = {
                        helmet: 'https://yamano-media.com/blog/helmet/',
                        crampon: 'https://yamano-media.com/blog/crampons/',
                        iceAxe: 'https://yamano-media.com/blog/iceaxe/',
                        tent: 'https://yamano-media.com/blog/tent-gear/',
                        winterGear: 'https://yamano-media.com/blog/hardshell/'
                      };
                      return links[k] ? (
                        <a key={k} href={links[k]} target="_blank" rel="noopener noreferrer" className="exp-tag exp-tag--checked" style={{textDecoration:'none'}}>
                          {labels[k] || k}
                        </a>
                      ) : (
                        <span key={k} className="exp-tag exp-tag--checked">{labels[k] || k}</span>
                      );
                    })}
                  </div>
                </div>
              )}

              {reviews.length > 0 && (
                <div className="reviews-section">
                  <label>受け取った評価 ({reviews.length}件)</label>
                  <div className="reviews-list">
                    {reviews.map(r => (
                      <div key={r.id} className={`review-item review-${r.rating}`}>
                        <span className="review-rating">{r.rating === 'good' ? '良かった' : '残念だった'}</span>
                        {r.comment && <p className="review-comment">{r.comment}</p>}
                        <p className="review-date">{r.createdAt?.toDate ? r.createdAt.toDate().toLocaleDateString('ja-JP') : ''}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sidebar">
          <div className="card">
            <h3>SNSリンク</h3>
            <div className="social-links">
              {profile?.yamapLink && (
                <a 
                  href={profile.yamapLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  🗺️ YAMAP
                </a>
              )}
              
              {profile?.yamarecoLink && (
                <a 
                  href={profile.yamarecoLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  ⛰️ ヤマレコ
                </a>
              )}
              
              {profile?.twitterLink && (
                <a 
                  href={profile.twitterLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  🐦‍🔷 X(Twitter)
                </a>
              )}
              
              {profile?.youtubeLink && (
                <a 
                  href={profile.youtubeLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  📺 YouTube
                </a>
              )}
              
              {profile?.instagramLink && (
                <a 
                  href={profile.instagramLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  📷 Instagram
                </a>
              )}
              
              {!profile?.yamapLink && !profile?.yamarecoLink && 
               !profile?.twitterLink && !profile?.youtubeLink && !profile?.instagramLink && (
                <p className="no-links">リンクが設定されていません</p>
              )}
            </div>
          </div>

          <div className="card">
            <h3>アカウント設定</h3>
            <div className="account-actions">
              {/* Google認証ユーザーにはメール・パスワード変更を非表示 */}
              {currentUser && currentUser.providerData[0].providerId !== 'google.com' && (
                <>
                  <Link to="/change-email" className="btn btn-outline">
                    メールアドレス変更
                  </Link>
                  <Link to="/change-password" className="btn btn-outline">
                    パスワード変更
                  </Link>
                </>
              )}
              <button 
                className="btn btn-danger"
                onClick={handleDeleteAccount}
              >
                アカウント削除
              </button>
              <button 
                className="btn btn-secondary"
                onClick={handleLogout}
              >
                ログアウト
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
